/**
 * useChess — authoritative chess state with Supabase realtime sync.
 *
 * Key fixes:
 *  1. processingRef ALWAYS released — every return path is inside try/finally
 *  2. isMyTurnNow() is a plain function reading refs — zero stale closure risk
 *  3. catchupNewMoves only applies moves not yet in appliedMoveNums (no overwrite race)
 *  4. makeMove deps array is [roomId] only — everything else read via stable refs
 */
import { useState, useEffect, useCallback, useRef } from 'react'
import { Chess } from 'chess.js'
import { supabase } from '@/lib/supabase.js'
import { saveChessMove, getChessMoves, clearChessMoves } from '@/utils/roomApi.js'

export function useChess(roomId, playerColor, gameActive) {
  const [fen, setFen] = useState(() => new Chess().fen())
  const [moveHistory, setMoveHistory] = useState([])
  const [gameOver, setGameOver] = useState(null)
  const [lastMove, setLastMove] = useState(null)
  const [promotionPending, setPromotionPending] = useState(null)
  const [syncing, setSyncing] = useState(false)

  const fenRef = useRef(new Chess().fen())
  const mountedRef = useRef(true)
  const processingRef = useRef(false)
  const appliedMoveNums = useRef(new Set())
  // Keep prop values in refs so callbacks always read current values
  const gameActiveRef = useRef(gameActive)
  const playerColorRef = useRef(playerColor)
  gameActiveRef.current = gameActive
  playerColorRef.current = playerColor

  useEffect(() => {
    mountedRef.current = true
    return () => { mountedRef.current = false }
  }, [])

  // ─── helpers ──────────────────────────────────────────────────────────────

  function deriveGameOver(g) {
    if (!g.isGameOver()) return null
    if (g.isCheckmate()) return { reason: 'checkmate', winner: g.turn() === 'w' ? 'black' : 'white' }
    if (g.isStalemate()) return { reason: 'stalemate', winner: null }
    if (g.isThreefoldRepetition()) return { reason: 'threefold_repetition', winner: null }
    if (g.isInsufficientMaterial()) return { reason: 'insufficient_material', winner: null }
    if (g.isDraw()) return { reason: 'fifty_move_rule', winner: null }
    return { reason: 'draw', winner: null }
  }

  /** Always reads live fenRef — never a stale closure. */
  function isMyTurnNow() {
    if (!gameActiveRef.current) return false
    try {
      const g = new Chess(fenRef.current)
      if (g.isGameOver()) return false
      const turn = g.turn()
      const color = playerColorRef.current
      return (turn === 'w' && color === 'white') || (turn === 'b' && color === 'black')
    } catch { return false }
  }

  // ─── initial load ─────────────────────────────────────────────────────────

  useEffect(() => {
    if (!roomId || !gameActive) return
    let cancelled = false

    async function loadMoves() {
      setSyncing(true)
      try {
        const { data: moves } = await getChessMoves(roomId)
        if (cancelled || !mountedRef.current || !moves?.length) return

        const g = new Chess()
        const history = []
        const nums = new Set()
        let lastApplied = null

        for (const m of moves) {
          try {
            const result = g.move({ from: m.from_square, to: m.to_square, promotion: m.promotion || undefined })
            if (result) {
              history.push({ san: result.san, color: result.color, moveNumber: m.move_number })
              nums.add(m.move_number)
              lastApplied = { from: m.from_square, to: m.to_square }
            }
          } catch (_) {}
        }

        if (cancelled || !mountedRef.current) return
        appliedMoveNums.current = nums
        const newFen = g.fen()
        fenRef.current = newFen
        setFen(newFen)
        setMoveHistory(history)
        setGameOver(deriveGameOver(g))
        if (lastApplied) setLastMove(lastApplied)
      } finally {
        if (!cancelled && mountedRef.current) setSyncing(false)
      }
    }

    loadMoves()
    return () => { cancelled = true }
  }, [roomId, gameActive]) // eslint-disable-line react-hooks/exhaustive-deps

  // ─── realtime subscription ────────────────────────────────────────────────

  useEffect(() => {
    if (!roomId || !gameActive) return

    const channel = supabase.channel(`chess:${roomId}`, {
      config: { broadcast: { self: false } },
    })

    channel
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'chess_moves', filter: `room_id=eq.${roomId}` },
        (payload) => {
          if (!mountedRef.current) return
          const move = payload.new

          // Skip our own moves — already applied optimistically
          if (appliedMoveNums.current.has(move.move_number)) return

          try {
            const g = new Chess(fenRef.current)
            const result = g.move({
              from: move.from_square,
              to: move.to_square,
              promotion: move.promotion || undefined,
            })
            if (!result || !mountedRef.current) return

            appliedMoveNums.current.add(move.move_number)
            const canonicalFen = move.fen || g.fen()
            fenRef.current = canonicalFen

            setFen(canonicalFen)
            setLastMove({ from: move.from_square, to: move.to_square })
            setMoveHistory((prev) => [
              ...prev,
              { san: result.san, color: result.color, moveNumber: move.move_number },
            ])
            setGameOver(deriveGameOver(new Chess(canonicalFen)))
          } catch (err) {
            console.error('[useChess] Failed to apply remote move:', err)
            if (mountedRef.current) recoverFromDesync()
          }
        }
      )
      .subscribe((status) => {
        // On subscribe, apply any moves missed during setup — additive only
        if (status === 'SUBSCRIBED') catchupNewMoves()
      })

    return () => { supabase.removeChannel(channel) }
  }, [roomId, gameActive]) // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Fetch DB moves and apply ONLY ones not already in appliedMoveNums.
   * Never overwrites existing state — purely additive.
   */
  async function catchupNewMoves() {
    if (!roomId || !mountedRef.current) return
    const { data: moves } = await getChessMoves(roomId)
    if (!moves || !mountedRef.current) return

    const hasNew = moves.some((m) => !appliedMoveNums.current.has(m.move_number))
    if (!hasNew) return // nothing to do — avoids overwriting optimistic state

    const g = new Chess()
    const history = []
    const nums = new Set()
    let lastApplied = null

    for (const m of moves) {
      try {
        const result = g.move({ from: m.from_square, to: m.to_square, promotion: m.promotion || undefined })
        if (result) {
          history.push({ san: result.san, color: result.color, moveNumber: m.move_number })
          nums.add(m.move_number)
          lastApplied = { from: m.from_square, to: m.to_square }
        }
      } catch (_) {}
    }

    if (!mountedRef.current) return
    appliedMoveNums.current = nums
    const newFen = g.fen()
    fenRef.current = newFen
    setFen(newFen)
    setMoveHistory(history)
    setGameOver(deriveGameOver(g))
    if (lastApplied) setLastMove(lastApplied)
  }

  async function recoverFromDesync() {
    console.warn('[useChess] Desync — recovering from DB')
    // Force full reload by clearing applied set
    appliedMoveNums.current = new Set()
    await catchupNewMoves()
  }

  // ─── make move ────────────────────────────────────────────────────────────

  const makeMove = useCallback(
    async (from, to, promotion = null) => {
      if (!isMyTurnNow()) return false
      if (processingRef.current) return false

      const myChar = playerColorRef.current === 'white' ? 'w' : 'b'
      let g
      try {
        g = new Chess(fenRef.current)
      } catch { return false }

      const piece = g.get(from)
      if (!piece || piece.color !== myChar) return false

      // Promotion detection — before locking processingRef
      const promotionRank = playerColorRef.current === 'white' ? '8' : '1'
      if (piece.type === 'p' && to[1] === promotionRank && !promotion) {
        const test = new Chess(fenRef.current)
        if (!test.move({ from, to, promotion: 'q' })) return false
        setPromotionPending({ from, to })
        return 'promotion_pending'
      }

      processingRef.current = true

      try {
        const moveResult = g.move({ from, to, promotion: promotion || undefined })

        if (!moveResult) {
          // Illegal move — lock released in finally
          return false
        }

        const newFen = g.fen()
        // Use the global half-move count derived from the FEN, not g.history().length.
        // g.history() counts from THIS instance's start — always 1 after one move,
        // causing both players to write move_number=1. The FEN fullMove + turn is unique.
        const fenParts = newFen.split(' ')
        const fullMove = parseInt(fenParts[5], 10)
        const turnAfter = fenParts[1] // 'w' or 'b' AFTER the move was made
        const moveNumber = (fullMove - 1) * 2 + (turnAfter === 'w' ? 2 : 1)

        // Mark applied BEFORE the await so the realtime echo skips it
        appliedMoveNums.current.add(moveNumber)
        fenRef.current = newFen

        // Optimistic UI
        setFen(newFen)
        setLastMove({ from, to })
        setPromotionPending(null)
        setMoveHistory((prev) => [...prev, { san: moveResult.san, color: moveResult.color, moveNumber }])
        setGameOver(deriveGameOver(g))

        await saveChessMove(roomId, {
          moveNumber,
          color: moveResult.color,
          san: moveResult.san,
          fen: newFen,
          from,
          to,
          promotion,
        })

        return true
      } catch (err) {
        console.error('[useChess] Move error:', err)
        return false
      } finally {
        processingRef.current = false  // ALWAYS released
      }
    },
    [roomId] // reads everything else via refs — no stale closure possible
  )

  // ─── promotion ────────────────────────────────────────────────────────────

  const handlePromotion = useCallback(
    (piece) => {
      if (!promotionPending) return
      const { from, to } = promotionPending
      setPromotionPending(null)
      makeMove(from, to, piece)
    },
    [promotionPending, makeMove]
  )

  // ─── reset ────────────────────────────────────────────────────────────────

  const resetGame = useCallback(async () => {
    if (!roomId) return
    processingRef.current = false
    await clearChessMoves(roomId)
    appliedMoveNums.current = new Set()
    const startFen = new Chess().fen()
    fenRef.current = startFen
    setFen(startFen)
    setMoveHistory([])
    setGameOver(null)
    setLastMove(null)
    setPromotionPending(null)
  }, [roomId])

  // ─── legal moves for UI ───────────────────────────────────────────────────

  const getLegalMovesFrom = useCallback((square) => {
    try { return new Chess(fenRef.current).moves({ square, verbose: true }) }
    catch { return [] }
  }, [])

  const getCurrentGame = useCallback(() => {
    try { return new Chess(fenRef.current) } catch { return new Chess() }
  }, [])

  // ─── derived render values ────────────────────────────────────────────────

  let currentTurn = 'white'
  let isCheck = false
  try {
    const g = new Chess(fen)
    currentTurn = g.turn() === 'w' ? 'white' : 'black'
    isCheck = g.isCheck()
  } catch (_) {}

  return {
    fen,
    moveHistory,
    gameOver,
    lastMove,
    promotionPending,
    syncing,
    currentTurn,
    isCheck,
    isMyTurn: isMyTurnNow(),
    getCurrentGame,
    makeMove,
    handlePromotion,
    resetGame,
    cancelPromotion: () => setPromotionPending(null),
    getLegalMovesFrom,
  }
}